import { Events } from './modules/Events.js';
import { SimpleModal, appModalContents } from './modules/SimpleModal.js';
import { AccountModal } from './modules/AccountModal.js';
import { CalendarSubscriptions } from './modules/CalendarSubscriptions.js';
import { Contact } from './modules/contact.js';
import { Home } from './modules/Home.js';
import { Account } from './services/Account.js';
import { ApiClient } from './services/ApiClient.js';
import { Notification } from './services/Notification.js';
import { translate } from './modules/i18n.js';

export class Budoevents {
    constructor(options = {}) {
        this.configUrl = options.configUrl || '/config.json';
        this.config = null;
        this.api = null;
        this.locale = options.locale || document.documentElement.lang || 'fr';
        this.events = null;
        this.simpleModal = null;
        this.account = null;
        this.accountModal = null;
        this.calendarSubscriptions = null;
        this.contact = null;
        this.home = null;
        this.organisationValidationInProgress = false;
        this.eventValidationInProgress = false;
        this.ready = false;
    }

    async boot() {
        this.config = await this.loadConfig();
        this.locale = this.resolveLocale();
        this.ready = true;

        document.documentElement.lang = this.locale;
        this.applyLocaleClass();
        document.body.dataset.appReady = 'true';
        this.renderVersion();
        Notification.init({
            parentSelector: 'body',
            containerSelector: '#notification',
            images: '/images'
        });
        const apiConfig = {
            ...(this.config.api || {}),
            publicToken: this.config.api?.publicToken || this.config.api?.generic_token || null
        };

        this.api = new ApiClient(apiConfig, this);
        this.account = new Account({
            app: this,
            api: this.api,
            authToken: apiConfig.publicToken,
            mode: 'web',
            platform: 'web'
        });
        this.accountModal = new AccountModal(this, this.account);
        this.calendarSubscriptions = new CalendarSubscriptions(this);
        this.contact = new Contact(this, { api: this.api });
        this.simpleModal = new SimpleModal(this, {
            ...appModalContents,
            ...this.accountModal.getContents(),
            ...this.calendarSubscriptions.getContents(),
            ...this.contact.getContents()
        });
        this.events = new Events(this, {api: this.api});
        await this.events.restoreFilters();
        this.home = new Home(this);
        this.home.init();
        await this.account.restoreSession();
        this.updateAuthMenu();
        await this.home.handleInitialRoute();
        this.home.loadFilteredEventsInBackground();
        await this.home.loadStats();
        await this.handleOrganisationValidationLink();
        await this.handleEventValidationLink();

        console.info(`${this.config.name || 'BUDO Events'} ready`);

        return this;
    }

    handleMenuSelection(contentId, item, trigger) {
        if (contentId === 'add-event') {
            this.events?.add();
            return true;
        }

        if (contentId === 'subscribe') {
            this.events?.subscribe(trigger || item);
            return true;
        }

        if (contentId === 'account') {
            return this.simpleModal.open('account', trigger || item);
        }

        if (contentId === 'signout') {
            this.account.signout({reload: false, delay: 0, boot: false}).then(() => {
                this.updateAuthMenu();
            });
            return true;
        }

        return this.simpleModal.open(contentId, trigger || item);
    }

    updateAuthMenu() {
        const authenticated = Boolean(this.user);

        document.querySelectorAll('[data-menu-item="signin"], [data-menu-item="signup"]').forEach((item) => {
            item.hidden = authenticated;
        });
        document.querySelectorAll('[data-menu-item="account"], [data-menu-item="signout"]').forEach((item) => {
            item.hidden = !authenticated;
        });
        this.home?.updateAuthState?.();
        this.home?.updateFilterBadge?.();
    }

    getOrganisationValidationUid() {
        const deepLink = new URLSearchParams(window.location.search).get('d') || '';
        const match = deepLink.match(/^organisation\/validate\/([0-9a-f-]{36})$/i);

        return match?.[1] || null;
    }

    async handleOrganisationValidationLink() {
        const uid = this.getOrganisationValidationUid();

        if (!uid || this.organisationValidationInProgress) return false;
        if (!this.user) {
            new Notification({
                icon: 'signin-grey.svg',
                title: translate('auth.organisationValidationAuthTitle', this.locale),
                content: translate('auth.organisationValidationAuthMessage', this.locale),
                delay: 8000
            });
            this.simpleModal?.open('signin');
            return false;
        }

        this.organisationValidationInProgress = true;
        try {
            await this.account.validateOrganisationMembership(uid);
            new Notification({
                icon: 'check.svg',
                title: translate('auth.organisationValidationSuccessTitle', this.locale),
                content: translate('auth.organisationValidationSuccessMessage', this.locale),
                delay: 7000
            });
            const url = new URL(window.location.href);
            url.searchParams.delete('d');
            window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
            return true;
        } catch (error) {
            new Notification({
                icon: 'warning-red.svg',
                title: translate('auth.organisationValidationErrorTitle', this.locale),
                content: error?.message || translate('auth.organisationValidationErrorMessage', this.locale),
                delay: 9000
            });
            return false;
        } finally {
            this.organisationValidationInProgress = false;
        }
    }

    getEventValidationLink() {
        const deepLink = new URLSearchParams(window.location.search).get('d') || '';
        const match = deepLink.match(/^event\/validate\/([0-9a-f-]{36})\/([0-9a-f-]{36})$/i);

        return match ? { uid: match[1], token: match[2] } : null;
    }

    async handleEventValidationLink() {
        const validation = this.getEventValidationLink();

        if (!validation || this.eventValidationInProgress) return false;
        if (!this.user) {
            new Notification({
                icon: 'signin-grey.svg',
                title: translate('eventValidation.authTitle', this.locale),
                content: translate('eventValidation.authMessage', this.locale),
                delay: 8000
            });
            this.simpleModal?.open('signin');
            return false;
        }
        if (String(this.user.class || '').toUpperCase() !== 'MASTER') {
            new Notification({
                icon: 'warning.svg',
                title: translate('eventValidation.forbiddenTitle', this.locale),
                content: translate('eventValidation.forbiddenMessage', this.locale),
                delay: 9000
            });
            return false;
        }

        await this.home?.showTab('list');
        await this.events?.openEventDetailsByUid(validation.uid);

        const confirmation = new Notification({
            icon: 'warning.svg',
            title: translate('eventValidation.confirmTitle', this.locale),
            content: translate('eventValidation.confirmMessage', this.locale),
            wait: true,
            close: true,
            buttons: [
                {
                    label: translate('eventValidation.cancel', this.locale),
                    class: 'ui-buttons cancel',
                    fct: () => Notification.remove(confirmation, 'now')
                },
                {
                    label: translate('eventValidation.confirmAction', this.locale),
                    class: 'ui-buttons',
                    fct: () => {
                        Notification.remove(confirmation, 'now');
                        void this.validateEvent(validation);
                    }
                }
            ]
        });
        const cleanUrl = new URL(window.location.href);

        window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.hash}`);

        return true;
    }

    async validateEvent({ uid, token }) {
        if (this.eventValidationInProgress) return false;
        this.eventValidationInProgress = true;
        let response = null;

        try {
            const succeeded = await this.events.api.query(
                'post',
                `/events/validate/${uid}/${token}`,
                {},
                null,
                (result) => {
                    response = result;
                }
            );
            if (!succeeded) return false;

            new Notification({
                icon: 'check.svg',
                title: translate('eventValidation.successTitle', this.locale),
                content: response?.message || translate('eventValidation.successMessage', this.locale),
                delay: 8000
            });
            const url = new URL(window.location.href);
            url.searchParams.delete('d');
            window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
            this.events.closeEventModal({ immediate: true });
            this.events.endEventValidationContext();
            await this.events.list();
            return true;
        } finally {
            this.eventValidationInProgress = false;
        }
    }

    applyLocaleClass() {
        const localeClass = `locale-${this.locale.toLowerCase().slice(0, 2)}`;

        Array.from(document.body.classList)
            .filter((className) => /^locale-[a-z]{2}$/.test(className))
            .forEach((className) => document.body.classList.remove(className));
        document.body.classList.add(localeClass);
    }

    renderVersion() {
        const version = this.config?.version || '';

        document.querySelectorAll('.app-version').forEach((element) => {
            element.textContent = version;
        });
    }

    async loadConfig() {
        const response = await fetch(this.configUrl, {
            cache: 'no-store',
            headers: {
                Accept: 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Unable to load ${this.configUrl}: ${response.status}`);
        }

        return response.json();
    }

    resolveLocale() {
        const fallbackLocale = this.config?.lg?.default || 'fr';
        const allowedLocales = this.config?.lg?.list || [fallbackLocale];
        const documentLocale = document.documentElement.lang || fallbackLocale;

        return allowedLocales.includes(documentLocale) ? documentLocale : fallbackLocale;
    }
}
